import { useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { getSubscriptionPriceInfo } from "@/types/api/user";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const Checkout = () => {
  const queryClient = useQueryClient();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/success`,
      },
    });

    if (error) {
      setMessage(error.message ?? "Une erreur s'est produite.");
    }

    await queryClient.invalidateQueries({ queryKey: ["me"] });

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || loading}
        className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
      >
        {loading ? "Chargement..." : "Payer"}
      </button>
      {message && <div className="text-red-500">{message}</div>}
    </form>
  );
};

const CheckoutPage = () => {
  const location = useLocation();
  const clientSecret = location.state?.clientSecret;
  const priceId = location.state?.priceId;

  if (!clientSecret) {
    return <div>Erreur: secret client manquant</div>;
  }

  if (!priceId) {
    return <div>Erreur: id du prix manquant</div>;
  }

  const subscriptionInfo = getSubscriptionPriceInfo(priceId);

  return (
    <div className="flex flex-col py-10 px-[10%] gap-8">
      <div className="font-bold text-2xl">
        {
          "Veuillez entrer vos coordonnées bancaires pour procéder au paiement de votre abonnement"
        }
      </div>
      <div>
        <div className="text-sm text-gray font-semibold">
          {"Abonnement choisi :"}
        </div>
        <div>{`${subscriptionInfo.label} ${subscriptionInfo.monthly ? "mensuel" : "annuel"}`}</div>
        <div>{`${subscriptionInfo.monthly ? subscriptionInfo.monthlyPrice : subscriptionInfo.yearlyPrice}€/${subscriptionInfo.monthly ? "mois" : "an"}`}</div>
      </div>
      <Elements
        stripe={stripePromise}
        options={{ clientSecret, appearance: { theme: "stripe" } }}
      >
        <Checkout />
      </Elements>
    </div>
  );
};

export default CheckoutPage;
